import { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { AuthContext } from "~/utils/useAuth";
import { styled, keyframes, vh, lightTheme, darkTheme } from "~/ui/style/stitches.config";
import { getPosts, getPreviewPosts, CommunityPost, PostsResponse } from "~/api/communityApi";
import { SimpleLoadingIndicator } from "~/pages/loading/LoadingPage";
import { motion, AnimatePresence } from "framer-motion";
import { trackEvent } from "~/api/analyticsApi";
import { Button } from "~/ui/components/Button";
import { CreatePostOverlay } from "./CreatePostOverlay";
import { imageUrl } from "~/utils/imageurl";
import { isIOSNativeApp } from "~/utils/deviceDetection";
import { localState } from "~/state/state";
import { useSnapshot } from "valtio";
import { BirthdayCollection } from "~/ui/components/auth/BirthdayCollection";
import { ChevronDownIcon, ChatBubbleIcon } from "@radix-ui/react-icons";


const fadeIn = keyframes({
  '0%': { opacity: 0, transform: 'translateY(10px)' },
  '100%': { opacity: 1, transform: 'translateY(0)' }
});

const filterOptions = [
  { value: "newest", label: "All posts" },
  { value: "popular", label: "Popular this month" },
  { value: "unanswered", label: "Unanswered posts" },
  { value: "mine", label: "Posts by you" },
  { value: "commented", label: "Interpreted by you" },
];

export const CommunityFeed = () => {
  const { appDarkMode: dk } = useSnapshot(localState);
  const { user, updateUser } = useContext(AuthContext);
  const [, setLocation] = useLocation();
  const [needsBirthday, setNeedsBirthday] = useState(false);
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [showCreateOverlay, setShowCreateOverlay] = useState(false);
  const [filter, setFilter] = useState("newest");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  // Check if user needs to provide birthday before accessing community
  useEffect(() => {
    if (user && !user.birthday) {
      setNeedsBirthday(true);
    }
  }, [user]);

  // Mark community as visited — clears the red dot indicator
  useEffect(() => {
    if (user) {
      localStorage.setItem('community_last_visited', String(Date.now()));
      localState.hasNewCommunityPosts = false;
    }
  }, [user]);

  // Close filter dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setPosts([]);
    setPage(1);
    setInitialLoadDone(false);
  }, [filter]);

  useEffect(() => {
    const fetchPosts = async () => {
      if (!user) return;

      try {
        setLoading(true);
        const data = await getPosts(page, filter);
        if (page === 1) {
          setPosts(data.posts);
        } else {
          setPosts(prev => [...prev, ...data.posts]);
        }
        setTotalPages(data.pagination.totalPages);
        setInitialLoadDone(true);
      } catch (error) {
        console.error('Failed to fetch community posts:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      if (page === 1) {
        trackEvent("/community/visited_page");
      }
      fetchPosts();
    }
  }, [user?.uuid, page, filter]);

  // Fetch preview posts for logged-out users
  useEffect(() => {
    if (user) return;
    const fetchPreview = async () => {
      try {
        setLoading(true);
        const data = await getPreviewPosts();
        setPosts(data.posts);
        setInitialLoadDone(true);
      } catch (error) {
        console.error('Failed to fetch preview posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPreview();
  }, [user]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
    const day = date.getDate();
    return { month, day };
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    const diffWeeks = Math.floor(diffDays / 7);
    if (diffWeeks <= 4) return `${diffWeeks}w`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const goToSignup = () => {
    setLocation("/signup?dest=/community");
  };

  const handleBirthdaySubmit = async (birthday: Date) => {
    try {
      await updateUser({ birthday: birthday.toISOString() });
      setNeedsBirthday(false);
    } catch (error) {
      console.error("Error saving birthday:", error);
    }
  };

  if (user && needsBirthday) {
    // birthday collection is always light mode, even mid-app like here
    return (
      <StyledBirthdayGate className={lightTheme.className}>
        <BirthdayCollection onSubmit={handleBirthdaySubmit} />
      </StyledBirthdayGate>
    );
  }

  return (
    <StyledCommunityPage>
      <Button
        Theme={dk ? "light" : "dark"}
        className="fixed-post-button"
        onClick={() => {
          if (!user) { goToSignup(); return; }
          setShowCreateOverlay(true);
        }}
        style={{ display: "flex", alignItems: "center", ...(isIOSNativeApp() ? { bottom: "210px" } : {}) }}
      >
        <img src="/images/edit.svg" style={{ width: "16px", height: "16px", marginRight: "5px", ...(dk && { filter: "invert(1)" }) }} />
        Post
      </Button>
      <StyledCommunityContent>
        <div className="page-header">
          <h1 className="page-title">Read for each other</h1>
          <p className="page-subtitle">
            <span style ={{ fontWeight: "bold" }}>New!</span> Practice your interpretation skills on each other's spreads. Or get a second (or third) take on yours.{" "}
            <span
              className="random-reading-link"
              onClick={() => {
                if (!user) { goToSignup(); return; }
                const othersPosts = posts.filter(p => p.authorId !== user?.uuid);
                if (othersPosts.length === 0) return;
                trackEvent("/community/interpret_random");
                const randomIndex = Math.floor(Math.random() * othersPosts.length);
                setLocation(`/post/${othersPosts[randomIndex].id}`);
              }}
            >
              Pick a random one.
            </span>
          </p>
        </div>

        {user && (
          <div className="filter-bar">
            <div className="filter-dropdown" ref={filterRef}>
              <div
                className={`filter-label ${isFilterOpen ? "open" : ""} ${filter !== "newest" ? "has-selection" : ""}`}
                onClick={() => setIsFilterOpen(!isFilterOpen)}
              >
                <span>
                  {filterOptions.find(o => o.value === filter)?.label ?? "All posts"}
                </span>
                <ChevronDownIcon
                  className="dropdown-arrow"
                  style={{
                    transform: isFilterOpen ? "rotate(-180deg)" : "rotate(0deg)",
                    transition: "all 0.2s ease-in-out",
                  }}
                />
              </div>
              {isFilterOpen && (
                <div className="filter-menu">
                  {filterOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`filter-option ${filter === option.value ? "selected" : ""}`}
                      onClick={() => {
                        setFilter(option.value);
                        setIsFilterOpen(false);
                      }}
                    >
                      {option.label}
                      {filter === option.value && (
                        <img className="checkmark" src="/images/more-menu/checkmark.svg" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="posts-container">
          {loading && !initialLoadDone ? (
            <div className="loading-container">
              <SimpleLoadingIndicator 
                style={{ 
                  position: 'static',
                  marginTop: '0',
                  width: '24px',
                  height: '24px',
                  opacity: 0.5,
                }} 
              />
            </div>
          ) : posts.length === 0 && initialLoadDone ? (
            <div className="empty-state">
              <div className="empty-title">
                {filter === "mine"
                  ? "You haven't posted yet."
                  : filter === "commented"
                  ? "You haven't interpreted any posts yet."
                  : filter === "unanswered"
                  ? "All posts have been interpreted!"
                  : filter === "popular"
                  ? "No posts in this time period."
                  : "No posts yet. Be the first to share a reading!"}
              </div>
              {filter === "newest" && (
                <Button
                  Theme={dk ? "light" : "dark"}
                  style={{
                    marginTop: "20px",
                    padding: "12px 20px",
                    fontSize: "12px",
                    borderRadius: "12px",
                  }}
                  onClick={() => {
                    setLocation("/create-room");
                  }}
                >
                  Open a room
                </Button>
              )}
            </div>
          ) : (
            <>
              {posts.map((post, index) => {
                const { month, day } = formatDate(post.createdAt);
                const commentCount = post._count?.comments || 0;
                
                return (
                  <PostCard
                    key={post.id}
                    as={motion.div}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: index < 10 ? index * 0.05 : 0 }}
                    onClick={() => {
                      if (!user) { goToSignup(); return; }
                      setLocation(`/post/${post.id}`);
                    }}
                  >
                    {/* Author info */}
                    <div className="post-header">
                      {post.author.profilePic ? (
                        <img
                          src={imageUrl(`/profile-pics/${post.author.profilePic}`, 80)}
                          alt={post.author.name}
                          className="author-pic"
                        />
                      ) : (
                        <div className="author-pic ghost-pic" />
                      )}
                      <div className="author-info">
                        <span className="author-name">{post.author.name}</span>
                        {user && <span className="post-time">{formatTimeAgo(post.createdAt)}</span>}
                      </div>
                    </div>

                    {/* Question */}
                    <div className="post-question">
                      {post.question.length > 200
                        ? `${post.question.slice(0, 200)}...`
                        : post.question}
                    </div>

                    {/* Cards preview */}
                    {post.reading?.cards && post.reading.cards.length > 0 && (
                      <div className="cards-row">
                        {post.reading.cards.slice(0, 5).map((card, cardIndex) => {
                          const isReversed = card.name.includes('Reversed');
                          return (
                            <div key={cardIndex} className="card-wrapper">
                              <img
                                src={card.imagePath?.replace(/w=\d+|w=undefined/, 'w=150')}
                                alt={card.name}
                                className="card-image"
                                style={{ transform: isReversed ? 'rotate(180deg)' : 'none' }}
                              />
                              {card.position && (
                                <div className="card-position">{card.position}</div>
                              )}
                            </div>
                          );
                        })}
                        {post.reading.cards.length > 5 && (
                          <div className="more-cards">+{post.reading.cards.length - 5}</div>
                        )}
                      </div>
                    )}

                    {/* Top comment preview */}
                    {post.topComment && (
                      <div className="top-comment">
                        <div className="comment-author-column">
                          {post.topComment.author.profilePic ? (
                            <img
                              src={imageUrl(`/profile-pics/${post.topComment.author.profilePic}`, 80)}
                              alt={post.topComment.author.name}
                              className="comment-author-pic"
                            />
                          ) : (
                            <div className="comment-author-pic ghost-pic" />
                          )}
                          <span className="comment-author-name">{post.topComment.author.name}</span>
                        </div>
                        <svg className="chat-triangle" width="12" height="18" viewBox="0 0 14 18">
                          <path d="M2 9L12 2V16L2 9Z" fill="#f5f5f5" stroke="#f5f5f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <div className="speech-bubble">
                          {post.topComment.content.length > 120
                            ? `${post.topComment.content.slice(0, 120)}...`
                            : post.topComment.content}
                        </div>
                      </div>
                    )}

                    {/* Invite bubble — shown when user hasn't commented yet */}
                    {user && !post.hasCommented && (
                      <div className="top-comment invite-bubble">
                        <div className="comment-author-column">
                          {user.profilePic ? (
                            <img
                              src={imageUrl(`/profile-pics/${user.profilePic}`, 80)}
                              alt={user.name}
                              className="comment-author-pic"
                            />
                          ) : (
                            <div className="comment-author-pic ghost-pic" />
                          )}
                          <span className="comment-author-name">{user.name?.split(' ')[0]}</span>
                        </div>
                        <svg className="chat-triangle" width="12" height="18" viewBox="0 0 14 18">
                          <path d="M2 9L12 2V16L2 9Z" fill="#f5f5f5" stroke="#f5f5f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <div className="speech-bubble" style={{ color: "#909090", fontFamily: "unset"}}>
                          Write your interpretation...
                        </div>
                      </div>
                    )}

                    {/* Footer with comment count */}
                    <div className="post-footer">
                      {post.hasCommented ? (
                        <div className="answered-label">Interpreted ✓</div>
                      ) : (
                        <span />
                      )}
                      <div className="comment-count">
                        <ChatBubbleIcon style={{ width: 14, height: 14, position: 'relative', top: 3 }} /> {commentCount}
                      </div>
                    </div>
                  </PostCard>
                );
              })}

              {/* Show more */}
              {page < totalPages && !loading && (
                <div className="show-more-container">
                  <button
                    className="show-more-btn"
                    onClick={() => setPage(p => p + 1)}
                  >
                    Show more
                  </button>
                </div>
              )}
              {loading && page > 1 && (
                <div className="loading-more">
                  <SimpleLoadingIndicator style={{
                    width: '20px',
                    height: '20px',
                    position: 'static',
                    top: 0,
                    opacity: 0.5
                  }} />
                </div>
              )}
            </>
          )}
        </div>

        {/* Logged-out fade overlay + CTA */}
        {!user && posts.length > 0 && (
          <div className="preview-overlay">
            <div className="preview-fade" />
            <div className="preview-cta">
              <Button
                Theme={dk ? "light" : "dark"}
                style={{
                  padding: "13px 20px",
                  fontSize: "12px",
                  borderRadius: "30px",
                }}
                onClick={goToSignup}
              >
                Join the community
              </Button>
            </div>
          </div>
        )}
      </StyledCommunityContent>

      <AnimatePresence>
        {showCreateOverlay && (
          <CreatePostOverlay
            onClose={() => setShowCreateOverlay(false)}
          />
        )}
      </AnimatePresence>
    </StyledCommunityPage>
  );
};

const StyledBirthdayGate = styled("div", {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: vh(100),
  backgroundColor: "$wash",

  "@notmobile": {
    paddingLeft: "$navWidth",
  },
});

const StyledCommunityPage = styled("div", {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  backgroundColor: "$wash",
  color: "#000",

  "@notmobile": {
    paddingLeft: "$navWidth",
  },

  "& .fixed-post-button": {
    position: "fixed",
    bottom: "62px",
    right: "26px",
    zIndex: 100,
    padding: "8px 18px",
    fontSize: "13px",
    borderRadius: "30px",
    border: "1px solid rgba(0, 0, 0, 0.9)",

    "@mobile": {
      bottom: "104px",
      right: "20px",
    },
  },
  [`.${darkTheme.className} &`]: {
    backgroundColor: "$appBg",
    color: "$text",
    "& .fixed-post-button": { borderColor: "rgba(255, 255, 255, 0.6)" },
  },
});

const StyledCommunityContent = styled("div", {
  padding: "40px",
  paddingTop: "80px",
  paddingBottom: "80px",

  "@mobile": {
    paddingLeft: "20px",
    paddingRight: "20px",
    paddingTop: "60px",
    paddingBottom: "80px",
  },

  "& .page-header": {
    backgroundColor: "rgb(230, 230, 230)",
    border: "1px solid black",
    width: "calc(90%)",
    margin: "-15px auto 34px auto",
    borderRadius: "33px",
    padding: "50px 20px",
    textAlign: "center",

    "@mobile": {
      width: "100%",
      margin: "10px auto 24px auto",
    },
  },

  "& .page-title": {
    fontFamily: "GorgeousBaby",
    fontWeight: "200",
    fontSize: "50px",
    marginBottom: "22px",
    marginLeft: "-3px",
    color: "black",
    "@mobile": {
      fontSize: "40px",
    },
  },

  "& .page-subtitle": {
    fontSize: "14px",
    fontWeight: "400",
    color: "black",
    maxWidth: "360px",
    margin: "0 auto",
    lineHeight: "1.5",

    "& .random-reading-link": {
      textDecoration: "underline",
      color: "$gray500",
      cursor: "pointer",
      transition: "opacity 0.2s ease",

      "&:hover": {
        opacity: 0.6,
      },
    },
  },

  position: "relative",

  "& .filter-bar": {
    display: "flex",
    justifyContent: "center",
    marginBottom: "28px",
  },

  "& .filter-dropdown": {
    position: "relative",
    width: "200px",
    color: "rgb(0, 0, 0, 0.8)",
  },

  "& .filter-label": {
    cursor: "pointer",
    height: "36px",
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    border: "1px solid rgba(0, 0, 0, 0.4)",
    borderRadius: "48px",
    backgroundColor: "white",
    transition: "border-color 0.2s ease",
    position: "relative",

    "&:hover, &.open": {
      borderColor: "rgba(0, 0, 0, 0.6)",
    },

    "&.has-selection": {
      borderColor: "rgba(0, 0, 0, 0.6)",
    },

    "& .dropdown-arrow": {
      position: "absolute",
      right: "12px",
      width: "16px",
      height: "16px",
    },
  },

  "& .filter-menu": {
    position: "absolute",
    top: "40px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "220px",
    backgroundColor: "white",
    border: "1px solid rgba(0, 0, 0, 0.6)",
    borderRadius: "18px",
    padding: "16px 8px",
    zIndex: 10,
  },

  "& .filter-option": {
    cursor: "pointer",
    marginTop: "2px",
    padding: "10px 8px",
    borderRadius: "7px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "12px",

    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.05)",
    },

    "& .checkmark": {
      width: "14px",
      height: "14px",
    },
  },

  "& .posts-container": {
    maxWidth: "520px",
    margin: "0 auto",
    paddingBottom: "80px",

    "@mobile": {
      paddingBottom: "220px",
    },
  },

  "& .preview-overlay": {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    pointerEvents: "none",
  },

  "& .preview-fade": {
    height: "300px",
    background: "linear-gradient(to bottom, transparent, $wash 80%)",
  },

  "& .preview-cta": {
    display: "flex",
    justifyContent: "center",
    backgroundColor: "$wash",
    marginTop: "-40px",
    paddingBottom: "140px",
    pointerEvents: "auto",

    "@mobile": {
      paddingBottom: "180px",
    },
  },

  "& .loading-container": {
    display: "flex",
    justifyContent: "center",
    marginTop: "60px",
  },

  "& .empty-state": {
    textAlign: "center",
    marginTop: "60px",

    "& .empty-title": {
      fontSize: "13px",
      lineHeight: "1.5",
      margin: "0 auto",
      maxWidth: "280px",
    },
  },

  "& .show-more-container": {
    display: "flex",
    justifyContent: "center",
    marginTop: "24px",
  },

  "& .show-more-btn": {
    padding: "9px 20px",
    fontSize: "11px",
    border: "none",
    borderRadius: "20px",
    backgroundColor: "#e4e4e4",
    cursor: "pointer",
    transition: "all 0.2s ease",

    "&:hover": {
      backgroundColor: "#000",
      color: "#fff",
    },
  },

  "& .loading-more": {
    display: "flex",
    justifyContent: "center",
    marginTop: "24px",
  },
  [`.${darkTheme.className} &`]: {
    "& .page-header": { backgroundColor: "$appBg", borderColor: "rgba(255, 255, 255, 0.6)" },
    "& .page-title, & .page-subtitle": { color: "$text" },
    "& .page-subtitle .random-reading-link": { color: "$textMuted" },
    "& .filter-dropdown": { color: "$text" },
    "& .filter-label, & .filter-menu": { backgroundColor: "$surface", color: "$text", borderColor: "rgba(255, 255, 255, 0.4)" },
    "& .filter-label:hover, & .filter-label.open, & .filter-label.has-selection": { borderColor: "#fff" },
    "& .filter-option:hover": { backgroundColor: "rgba(255, 255, 255, 0.12)" },
    "& .filter-option .checkmark": { filter: "invert(1)" },
    "& .preview-fade": { background: "linear-gradient(to bottom, transparent, var(--colors-appBg) 80%)" },
    "& .preview-cta": { backgroundColor: "$appBg" },
    "& .empty-state": { color: "$text" },
    "& .show-more-btn": { backgroundColor: "$subtleFill", color: "$text", "&:hover": { backgroundColor: "#fff", color: "#000" } },
  },
});

const PostCard = styled("div", {
  position: "relative",
  backgroundColor: "rgba(255, 255, 255, 1)",
  border: "1px solid rgba(0, 0, 0, 0.2)",
  borderRadius: "26px",
  padding: "20px",
  marginBottom: "16px",
  cursor: "pointer",
  transition: "all 0.2s ease",

  "&:hover": {
    transform: "translateY(2px)",
  },

  "& .post-header": {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "12px",
  },

  "& .author-pic": {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    objectFit: "cover",
  },

  "& .ghost-pic": {
    backgroundColor: "#4a4a4a",
  },

  "& .author-info": {
    display: "flex",
    flexDirection: "column",
  },

  "& .author-name": {
    fontSize: "12px",
    fontWeight: "500",
  },

  "& .post-time": {
    fontSize: "10px",
    color: "$gray500",
    letterSpacing: "0.02em",
  },

  "& .post-question": {
    fontSize: "14px",
    lineHeight: "1.3",
    margin: "14px 0 18px 0",
    fontFamily: "Arial, sans-serif",
  },

  "& .cards-row": {
    display: "flex",
    gap: "10px",
    marginBottom: "12px",
    flexWrap: "wrap",
    alignItems: "flex-start",
  },

  "& .card-wrapper": {
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    maxWidth: "56px",
  },

  "& .card-image": {
    width: "45px",
    height: "auto",
    borderRadius: "4px",
    border: "1px solid rgba(0, 0, 0, 0.2)",
  },

  "& .card-position": {
    fontSize: "9px",
    color: "rgb(0, 0, 0, 0.6)",
    letterSpacing: "0.01em",
    marginTop: "4px",
    textAlign: "center",
    lineHeight: "1.1",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    wordBreak: "break-word",
  },

  "& .more-cards": {
    fontSize: "10px",
    color: "$gray800",
    padding: "4px 9px",
    backgroundColor: "#f0f0f0",
    borderRadius: "14px",
    alignSelf: "center",
    marginTop: "-12px",
  },

  "& .top-comment": {
    display: "flex",
    alignItems: "flex-start",
    gap: "4px",
    marginTop: "18px",
    marginBottom: "10px",
  },

  "& .comment-author-column": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "50px",
    flexShrink: 0,
  },

  "& .comment-author-pic": {
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    objectFit: "cover",
  },

  "& .comment-author-name": {
    fontSize: "9px",
    fontWeight: "400",
    marginTop: "4px",
    textAlign: "center",
    lineHeight: "1.2",
    wordBreak: "break-word",
    maxWidth: "50px",
  },

  "& .chat-triangle": {
    marginTop: "10px",
    flexShrink: 0,
    marginRight: "-8px",
    zIndex: 1,
  },

  "& .speech-bubble": {
    backgroundColor: "#f5f5f5",
    padding: "12px 14px",
    borderRadius: "16px",
    fontSize: "12px",
    lineHeight: "1.4",
    fontFamily: "Arial, sans-serif",
    color: "#333",
    flex: 1,
    minWidth: 0,
  },

  "& .invite-bubble": {
    marginTop: "8px",
  },

  "& .post-footer": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: "6px",
    paddingBottom: "4px",
  },

  "& .add-take-link": {
    fontSize: "11px",
    color: "$gray800",
    fontWeight: "500",
    padding: "5px 12px",
    borderRadius: "14px",
    cursor: "pointer",
    background: "rgba(0, 0, 0, 0.04)",
    "&:hover": {
      background: "rgba(0, 0, 0, 0.1)",
    },
  },

  "& .answered-label": {
    fontSize: "11px",
    color: "$gray400",
  },

  "& .no-comments": {
    fontSize: "11px",
    color: "$gray800",
  },

  "& .comment-count": {
    fontSize: "12px",
    color: "$gray800",
    "&:hover": {
      opacity: 0.6,
    },
  },
  [`.${darkTheme.className} &`]: {
    backgroundColor: "#262626",
    borderColor: "rgba(255, 255, 255, 0.4)",
    color: "$text",
    "& .post-time, & .answered-label": { color: "$textMuted" },
    "& .card-image": { borderColor: "rgba(255, 255, 255, 0.25)" },
    "& .card-position": { color: "$text", opacity: 0.7 },
    "& .more-cards": { backgroundColor: "$subtleFill", color: "$text" },
    "& .speech-bubble": { backgroundColor: "#000", color: "$text" },
    "& .chat-triangle path": { fill: "#000", stroke: "#000" },
    "& .add-take-link": { color: "$text", background: "rgba(255, 255, 255, 0.1)", "&:hover": { background: "rgba(255, 255, 255, 0.2)" } },
    "& .no-comments": { color: "$textMuted" },
    "& .comment-count": { color: "#fff" },
  },
});
